<template>
  <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
    <component
      :is="componentType"
      class="tile"
      :class="[{ 'theme-black': isDarkTheme }, { 'tile-link': linkHref }]"
      v-bind="propTypes"
    >
      <div class="tile-image">
        <sc-image v-if="isEditMode" :media="imageField" />
        <img v-else class="img" :src="imageSrc" :alt="imageAlt" />
      </div>
      <div v-if="showDescription" class="tile-description">
        <heading
          v-if="hasLargeHeader"
          class="large-header"
          desktopSize="size-3"
          mobileSize="size-4"
          :tagField="headingSizeField"
          :textField="largeHeaderField"
        />
        <heading
          v-if="hasTitle"
          class="title"
          desktopSize="size-3"
          mobileSize="size-4"
          :tagField="titleHeadingSizeField"
          :textField="titleField"
        />
        <sc-text
          v-if="hasTag"
          class="tag"
          tag="p"
          :field="tagField"
        />
      </div>
      <div v-if="linkHref" class="cta">
        <sh-button
          tabindex="-1"
          :naked="isNaked"
          :color="buttonColor"
        >
          {{ ctaField.text }}
        </sh-button>
      </div>
    </component>
  </sc-render>
</template>

<script>
import { Image, Text } from '@sitecore-jss/sitecore-jss-vue'
import { createNamespacedHelpers } from 'vuex'

import CTA from '~/constants/appearance'
import ShLink from '~/components/sh-link'
import mapFields from '~/helpers/map-fields'
import { decreaseTagValue } from '~/helpers/decrease-tag-value'

import ScRender from '../sc-render/'
import Heading from '../heading'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')
const { mapGetters: mapEnvGetters } = createNamespacedHelpers('ui/environment')

export default {
  name: 'TileImage',
  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },
  components: {
    ScImage: Image,
    ScRender,
    ShLink,
    ScText: Text,
    Heading
  },
  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapEnvGetters(['isDarkTheme']),
    ...mapFields({
      imageField: 'image',
      imageValue: 'image.value',
      imageAlt: 'image.value.alt',
      ctaField: 'cta.value',
      ctaAppearance: 'ctaAppearance.value',
      linkHref: 'cta.value.href',
      linkType: 'cta.value.linktype',
      linkTarget: 'cta.value.target',
      largeHeaderField: 'largeHeader',
      largeHeaderValue: 'largeHeader.value',
      titleField: 'title',
      titleValue: 'title.value',
      tagField: 'tag',
      tagValue: 'tag.value',
      headingSizeField: 'headingSize'
    }),

    themeClass () {
      return this.isDarkTheme ?
        'theme-black' :
        ' '
    },
    titleHeadingSizeField () {
      return decreaseTagValue(this.headingSizeField)
    },

    componentType () {
      if (!this.linkHref || this.isEditMode) {
        return 'div'
      }

      return 'sh-link'
    },

    propTypes () {
      if (this.componentType === 'div') {
        return {}
      }

      return {
        tabindex: '0',
        to: this.linkHref,
        target: this.linkTarget
      }
    },

    buttonColor () {
      return this.isDarkTheme ? CTA.SECONDARY : CTA.PRIMARY
    },

    hasLargeHeader () {
      return this.isEditMode || this.largeHeaderValue
    },

    hasTitle () {
      return this.isEditMode || this.titleValue
    },

    hasTag () {
      return this.isEditMode || this.tagValue
    },

    showDescription () {
      return this.isEditMode ||
        this.hasLargeHeader ||
        this.hasTitle ||
        this.hasTag
    },

    isNaked () {
      return this.ctaAppearance !== CTA.BUTTON
    },

    imageSrc () {
      if (!this.imageField) {
        return ''
      }

      return this.imageValue[this.aspectRatioKey] || this.imageValue.src
    },

    // calculate images aspect ratio due to current cloudinary requirements
    aspectRatioKey () {
      const w = this.imageValue.width
      const h = this.imageValue.height

      function calculateDimension (a, b) {
        return (b === 0) ? a : calculateDimension(b, a % b)
      }

      const dimension = calculateDimension(w, h)
      const aspectRatioWidth = w / dimension
      const aspectRatioHeight = h / dimension

      return `cloudinary_${aspectRatioWidth}x${aspectRatioHeight}_fill_crop`
    }
  }
}
</script>

<style lang="scss" scoped>

.tile {
  display: block;

  .tile-image {
    .img {
      display: block;
      width: 100%;
    }

    ::v-deep .sc-image-wrapper img {
      width: 100%;
      height: auto;
    }
  }

  .tile-description {
    padding: 0 $space-050;
    padding-top: $space-060;
    @include stack-space($space-020);

    @include tablet () {
      padding: 0;
      padding-top: $space-050;
    }

    .large-header {
      @include font('080');
      @include stack-space($space-015);

      @include tablet () {
        @include font('090');
      }
    }

    .title {
      @include font('070');
      @include stack-space($space-030);
    }

    .tag {
      @include font('040');

      color: $color_gray;
    }
  }

  .cta {
    padding: 0 $space-050;

    @include tablet {
      padding: 0;
    }
  }
}

.tile-link {
  text-decoration: none;
  cursor: pointer;
}

.theme-black {
  .tile {
    @include outline-focus($color-white)
  }
}
</style>
